import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    NoteTypeBreadcrumbs,
    NoteTypeCreateBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import { SettingsGeneralSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as NoteTypeActions from "../../../actions/noteType";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    NumberFormInput,
    TextFormInput,
} from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { LookupsLayout } from "../../index";

import "./NoteTypePage.scss";

interface NoteTypePageProps {
    form: Dtos.NoteType & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    noteType: Dtos.NoteType;
    lookups: Dtos.Lookup[];
    loadingNoteType: boolean;
    loadNoteTypeFailure: boolean;
    loadNoteTypeErrorCode: string;
    savingNoteType: boolean;
    saveNoteTypeSuccess: boolean;
    saveNoteTypeFailure: boolean;
    creatingNoteType: boolean;
    createNoteTypeSuccess: boolean;
    validatingNoteType: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearNoteType: NoteTypeActions.INoteTypeClearActionCreator;
    loadNoteType: NoteTypeActions.INoteTypeLoadByIdActionCreator;
    saveNoteType: NoteTypeActions.INoteTypeSaveActionCreator;
    createNoteType: NoteTypeActions.INoteTypeCreateActionCreator;
    validateNoteType: NoteTypeActions.INoteTypeValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.noteType";

const NoteTypePage: FC<NoteTypePageProps> = ({
    form,
    formState,
    formProperties,
    noteType,
    lookups,
    loadingNoteType,
    loadNoteTypeFailure,
    loadNoteTypeErrorCode,
    savingNoteType,
    saveNoteTypeSuccess,
    saveNoteTypeFailure,
    creatingNoteType,
    createNoteTypeSuccess,
    validatingNoteType,
    validationFailures,
    permissions,
    clearNoteType,
    loadNoteType,
    saveNoteType,
    createNoteType,
    validateNoteType,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { notetypesId } = useParams<{ notetypesId: string }>();
    const noteTypeIdNumber = parseInt(notetypesId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.NoteType = {} as Dtos.NoteType;

        loadForm(reduxFormName, Object.assign({ ...noteType }, extraData));
    };


    // Load note type
    useEffect(() => {
        if (noteTypeIdNumber && !noteType && !loadingNoteType) {
            setDrawerItems(SettingsGeneralSubMenu(permissions));
            setBreadcrumbs(NoteTypeBreadcrumbs(noteTypeIdNumber));
            loadNoteType(noteTypeIdNumber);
        } else if (
            !noteTypeIdNumber &&
            !creatingNoteType &&
            !createNoteTypeSuccess
        ) {
            createNoteType();
            setBreadcrumbs(NoteTypeCreateBreadcrumbs());
            setDrawerItems([]);
        } else if (noteType && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        noteTypeIdNumber,
        noteType,
        setDrawerItems,
        SettingsGeneralSubMenu,
        permissions,
        setBreadcrumbs,
        NoteTypeCreateBreadcrumbs,
        loadNoteType,
        createNoteType,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingNoteType,
        creatingNoteType,
        createNoteTypeSuccess,
        NoteTypeBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadNoteTypeFailure) {
            navigate(`/error/${loadNoteTypeErrorCode}`);
        }
    }, [loadNoteTypeFailure, loadNoteTypeErrorCode, navigate]);

    // Update breadcrumbs
    useEffect(() => {
        if (noteTypeIdNumber && noteType?.value) {
            setBreadcrumbs(
                NoteTypeBreadcrumbs(noteTypeIdNumber, noteType?.value)
            );
        }
    }, [noteTypeIdNumber, noteType, setBreadcrumbs, NoteTypeBreadcrumbs]);

    // Reload note type after saving
    useEffect(() => {
        if (!savingNoteType && saveNoteTypeSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Note type saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string = "/settings/general/notetypes";
                navigate(url);
            } else if (!noteTypeIdNumber) {
                let url: string = `/settings/general/notetypes/${noteType?.id}`;
                navigate(url);
                clearNoteType();
            }
        }
    }, [
        savingNoteType,
        saveNoteTypeSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingNoteType && saveNoteTypeFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingNoteType, saveNoteTypeFailure]);

    // Cleanup effect to clear the note type on component unmount
    useEffect(() => {
        return () => {
            clearNoteType();
            resetForm(reduxFormName);
        };
    }, [clearNoteType, resetForm, reduxFormName]);

    const onCancel = () => {
        let url: string = "/settings/general/notetypes";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.NoteType) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.NoteType = {} as Dtos.NoteType;

            await saveNoteType(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.NoteType = {} as Dtos.NoteType;

            if (form && hasBeenSubmitted && !validatingNoteType) {
                await validateNoteType(Object.assign({ ...form }, changes));
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.NoteTypesUpdate,
                Dtos.Permission.NoteTypesCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingNoteType ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "noteType",
                                        {
                                            value: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "noteType",
                                        {
                                            rank: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    {renderFormControls(
                                        form,
                                        "noteType",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "noteType",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingNoteType,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
    ]);

    return (
        <LookupsLayout
            loading={loadingNoteType}
            permission={Dtos.Permission.NoteTypesView}
        >
            {renderContent}
        </LookupsLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<NoteTypePageProps> => {
    let noteType: Dtos.NoteType | undefined = !(
        state.noteTypes.formData instanceof Array
    )
        ? state.noteTypes.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        noteType: noteType!,
        loadingNoteType:
            state.noteTypes.loadState &&
            state.noteTypes.loadState.status == RequestState.Pending,
        loadNoteTypeFailure:
            state.noteTypes.loadState &&
            state.noteTypes.loadState.status == RequestState.Failure,
        loadNoteTypeErrorCode: state.noteTypes.loadState.errorCode,
        savingNoteType:
            state.noteTypes.saveState &&
            state.noteTypes.saveState.status == RequestState.Pending,
        saveNoteTypeSuccess:
            state.noteTypes.saveState &&
            state.noteTypes.saveState.status == RequestState.Success,
        saveNoteTypeFailure:
            state.noteTypes.saveState &&
            state.noteTypes.saveState.status == RequestState.Failure,
        creatingNoteType:
            state.noteTypes.createState &&
            state.noteTypes.createState.status == RequestState.Pending,
        createNoteTypeSuccess:
            state.noteTypes.createState &&
            state.noteTypes.createState.status == RequestState.Success,
        validatingNoteType:
            state.noteTypes.validateState &&
            state.noteTypes.validateState.status == RequestState.Pending,
        permissions: permissions,
        lookups: [],
        form: state.reduxForms.noteType,
        formState: state.noteTypes.formState,
        formProperties: state.noteTypes.formProperties,
        validationFailures: noteType
            ? state.noteTypes.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<NoteTypePageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadNoteType: bindActionCreators(
            NoteTypeActions.LoadNoteTypeById,
            dispatch
        ),
        clearNoteType: bindActionCreators(NoteTypeActions.Clear, dispatch),
        saveNoteType: bindActionCreators(
            NoteTypeActions.SaveNoteType,
            dispatch
        ),
        createNoteType: bindActionCreators(
            NoteTypeActions.CreateNoteType,
            dispatch
        ),
        validateNoteType: bindActionCreators(
            NoteTypeActions.ValidateNoteType,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(NoteTypePage);
